/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MentorFeedback } from '@app/app/page-modules/admin/services/mentor-mentee/mentor-mentee-data.service';
import {
  EditorContent,
  ExternalContentSummary,
  FileEditorContent,
  MediaType,
  MediaUploadData,
  MediaUrlData,
  UserPlaylistSubmissionSummary,
} from '../../models';

@Component({
  selector: 'ptl-resource-preview',
  templateUrl: './preview.component.html',
  styleUrls: ['./preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourcePreviewComponent implements OnChanges {
  @Input() previewContent: EditorContent[] = [];
  @Input() userPlaylistSubmissionSummary: UserPlaylistSubmissionSummary;
  @Input() userPlaylistAllMentorsFeedback?: MentorFeedback[];
  @Input() hideTextFormButtons = false;
  @Input() folioAttachmentPreviewView = false;
  @Input() isCollectorTemplateForm = false;
  @Input() canEditCollectorTemplateForm = false;
  @Input() includeSelfFeedback: boolean;
  @Input() withTruncateLines = false;
  @Input() collage = false;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.previewContent) {
      this.previewContent = changes.previewContent.currentValue;
    }
    if (changes.userPlaylistSubmissionSummary && changes.userPlaylistSubmissionSummary.currentValue) {
      this.cd.detectChanges();
    }
  }

  isContentHtmlPreview(content: EditorContent): boolean {
    return (
      this.isNonImageMediaUrl(content) ||
      content.type === 'PARAGRAPH' ||
      content.type === 'SEPARATOR' ||
      content.type === 'EMBED_CODE' ||
      content.type === 'TABLE'
    );
  }

  isContentImagePreview(content: EditorContent): boolean {
    return (content.type === 'MEDIA_URL' || content.type === 'MEDIA_UPLOAD') && (content as MediaUrlData).mediaType === 'IMAGE';
  }

  isContentStreamVideoPreview(content: EditorContent): boolean {
    return (content.type === 'MEDIA_URL' || content.type === 'MEDIA_UPLOAD') && (content as MediaUrlData).mediaType === 'STREAM_VIDEO';
  }

  isContentVideoPreview(content: EditorContent): boolean {
    return (content.type === 'MEDIA_URL' || content.type === 'MEDIA_UPLOAD') && (content as MediaUrlData).mediaType === 'VIDEO';
  }

  isContentAudioPreview(content: EditorContent): boolean {
    return (content.type === 'MEDIA_URL' || content.type === 'MEDIA_UPLOAD') && (content as MediaUrlData).mediaType === 'AUDIO';
  }

  isContentDocumentPreview(content: EditorContent): boolean {
    return content.type === 'MEDIA_UPLOAD' && (content as MediaUploadData).mediaType === 'DOCUMENT';
  }

  trackByFn(index: number, { uid }: EditorContent) {
    return uid || index;
  }

  getUrl(content: EditorContent): string {
    return (content as MediaUrlData)?.url ?? content?.content?.url;
  }

  getText(content: EditorContent): string {
    return (
      (content as FileEditorContent)?.caption ||
      (content as MediaUploadData)?.name ||
      (content as ExternalContentSummary)?.title ||
      this.getUrl(content)
    );
  }

  private isNonImageMediaUrl(content: EditorContent): boolean {
    return content.type === 'MEDIA_URL' && this.isNotImage(content as MediaUrlData);
  }

  private isNotImage(content: MediaUrlData): boolean {
    const mediaType: MediaType = content.mediaType;
    return mediaType !== 'IMAGE';
  }

  getImageContents(): EditorContent[] {
    return this.previewContent.filter((content) => this.isContentImagePreview(content));
  }

  shouldShowCollage(): boolean {
    return this.collage && this.getImageContents().length > 1;
  }

  getCollageClass(): string {
    const imageCount = this.getImageContents().length;
    if (imageCount <= 1) return '';
    if (imageCount <= 5) return `collage-${imageCount}`;
    return 'collage-6-plus';
  }

  getDisplayedImages(): EditorContent[] {
    const images = this.getImageContents();
    if (images.length <= 5) return images;
    return images.slice(0, 5);
  }

  getRemainingCount(): number {
    const images = this.getImageContents();
    return Math.max(0, images.length - 5);
  }

  getFirstImageIndex(): number {
    return this.previewContent.findIndex((content) => this.isContentImagePreview(content));
  }
}
